export type Lang = 'sv' | 'en'

/** Written when the visitor picks a language, so the choice is visible to a later visit. */
export const LANG_STORAGE_KEY = 'ronin-site-lang'

export type PageKey = 'home' | 'contact' | 'news' | 'privacy' | 'terms'

/**
 * The site in both languages, as data.
 *
 * Swedish sits at the root and English under /en, and both are prerendered to real
 * HTML. That is the whole reason the pair is a table rather than a runtime toggle: a
 * crawler, an App Store reviewer and a person who was sent a link all need the page
 * they asked for to exist at its own URL, in the language it claims. `seo.ts` builds
 * the hreflang alternates from this, `router.tsx` builds the routes, and
 * `LangToggle` uses it to point at the current page's twin.
 */
export const PAGES: Record<PageKey, Record<Lang, string>> = {
  home: { sv: '/', en: '/en' },
  contact: { sv: '/kontakt', en: '/en/contact' },
  news: { sv: '/nyheter', en: '/en/news' },
  privacy: { sv: '/integritet', en: '/en/privacy' },
  terms: { sv: '/villkor', en: '/en/terms' },
}

export const PAGE_KEYS = Object.keys(PAGES) as PageKey[]

/** Trailing slashes are stripped so "/villkor/" and "/villkor" are the same page. */
export function normalisePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.replace(/\/+$/, '')
  return pathname
}

export function pageForPath(pathname: string): { key: PageKey; lang: Lang } | null {
  const path = normalisePath(pathname)
  for (const key of PAGE_KEYS) {
    if (PAGES[key].sv === path) return { key, lang: 'sv' }
    if (PAGES[key].en === path) return { key, lang: 'en' }
  }
  return null
}

/**
 * The language a URL is in, including URLs that match no page.
 *
 * Everything English lives under /en, so the prefix answers it for the 404 too, where
 * there is no page entry to look up. That is the reason the English pages are a path
 * prefix rather than, say, a query parameter.
 */
export function langForPath(pathname: string): Lang {
  const path = normalisePath(pathname)
  return path === '/en' || path.startsWith('/en/') ? 'en' : 'sv'
}

/**
 * The same page in the other language. An unknown path (the 404) has no twin, so the
 * toggle falls back to that language's home page rather than disappearing.
 */
export function counterpartPath(pathname: string, target: Lang): string {
  const page = pageForPath(pathname)
  return page ? PAGES[page.key][target] : PAGES.home[target]
}

export function otherLang(lang: Lang): Lang {
  return lang === 'sv' ? 'en' : 'sv'
}

/** <html lang> values. Regional, because the Swedish half is written for Sweden. */
export const HTML_LANG: Record<Lang, string> = { sv: 'sv', en: 'en' }
export const OG_LOCALE: Record<Lang, string> = { sv: 'sv_SE', en: 'en_GB' }
